import React, { useEffect } from 'react';
import { change as changeFormField, Field } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { fieldRenderer } from 'utils/formRenderers';
import { validateTaxpayerId } from '@/actions/purchase';
import { useDispatch } from 'react-redux';
import { When } from 'react-if';
import useForm from 'hooks/store/useForm';
import usePurchase from 'hooks/store/usePurchase';
import LabelSwitch from '../../../ui/atoms/LabelSwitch';

/**
 * TaxpayerIdInput component
 *
 * This component renders a form section for inputting taxpayer information.
 * It includes a checkbox to indicate if an invoice is needed, and conditional
 * fields for RUC (taxpayer ID) and legal name.
 *
 * @returns {React.Component} The TaxpayerIdInput component
 */
function TaxpayerIdInput() {
  const { t } = useTranslation('passengers');
  const dispatch = useDispatch();
  const { purchaser } = useForm();
  const { fetchingTaxpayerId } = usePurchase();

  /**
   * Handles changes to the document ID input.
   * Dispatches an action to validate the taxpayer ID.
   *
   * @param {string} newValue - The new value of the document ID input.
   */
  const handleDocumentIdChange = (newValue) => {
    // PRO2-128: the thunk rejects to signal an unresolvable RUC, but it has already written that
    // outcome where the buyer can see it — 'RUC Invalido' in the read-only legal-name field, which
    // `legalName` then blocks payment on. Nothing here consumes the rejection, so leaving it
    // unhandled reaches nothing but Sentry's `onunhandledrejection` handler, which this app enables
    // by default. A provider outage would then report one error per keystroke past eleven digits,
    // on what is now the default Quick Purchase path. Swallowed deliberately: a failed lookup is an
    // outcome of this flow, not a defect in it — the same distinction the discount-code work drew.
    Promise.resolve(dispatch(validateTaxpayerId(newValue))).catch(() => {});
  };

  const { invoice, taxpayerId } = purchaser.values;

  // PRO2-128: since the invoice block became reachable while collapsed, this effect runs on mount in
  // the DEFAULT Quick Purchase state, where `invoice` is undefined. It is safe only because
  // `PurchaserForm/index.js` never seeds `taxpayerId` into `initialValues`. Hydrating that field
  // from the purchase without also hydrating `invoice` would make every collapsed buyer silently
  // blank their own stored id on arrival.
  useEffect(() => {
    if (!invoice && !!taxpayerId) {
      dispatch(changeFormField('purchaser', 'taxpayerId', ''));
      dispatch(changeFormField('purchaser', 'taxpayerLegalName', ''));
      dispatch(changeFormField('purchaser', 'taxpayerAddress', ''));
    }
  }, [invoice, taxpayerId, dispatch]);

  return (
    <>
      <Field
        name="invoice"
        id="invoice"
        type="checkbox"
        component={({ input }) => (
          <LabelSwitch
            checked={!!input.value}
            onChange={input.onChange}
            id="need-invoice"
            label={t('label.need_invoice')}
          />
        )}
        defaultChecked={false}
      />
      <When condition={invoice}>
        <div className="flex gap-3">
          <Field
            autoComplete="off"
            type="text"
            name="taxpayerId"
            id="taxpayerId"
            placeholder={t('label.taxpayer_id')}
            maxLength={11}
            component={fieldRenderer}
            onChange={(event, newValue) => handleDocumentIdChange(newValue)}
            isDisabled={fetchingTaxpayerId}
          />
          <Field
            autoComplete="off"
            type="text"
            name="taxpayerLegalName"
            id="legal-name"
            placeholder={t('label.legal_name')}
            component={fieldRenderer}
            isDisabled
          />
        </div>
      </When>
    </>
  );
}

export default TaxpayerIdInput;
